import Image from 'next/image';
import Link from 'next/link';

interface CreatorCardType {
  name: string;
  avatarUrl?: string;
  isCategory?: boolean;
  link?: string;
  emoji?: string;
  borderColor?: string;
  bgColor?: string;
  textColor?: string;
}

const CreatorCard = ({
  name,
  avatarUrl,
  isCategory,
  link,
  emoji,
  borderColor,
  bgColor,
  textColor,
}: CreatorCardType) => {
  if (isCategory) {
    const content = (
      <div
        className="rounded-2xl border-2 p-6 text-center shadow-md transition-all duration-300 hover:-translate-y-2 hover:shadow-xl"
        style={{
          borderColor: borderColor,
          backgroundColor: bgColor,
        }}
      >
        <div className="mx-auto mb-3 flex size-[72px] items-center justify-center overflow-hidden rounded-full bg-gray-100 shadow-lg">
          {avatarUrl ? (
            <Image
              src={avatarUrl}
              alt={`${name}のアイコン`}
              width={72}
              height={72}
              className="size-full object-cover"
            />
          ) : (
            <span className="text-4xl">{emoji}</span>
          )}
        </div>
        <h3 className="mb-1 text-lg font-bold" style={{ color: textColor }}>
          {name}
        </h3>
        <p className="text-xs text-pp-text-tertiary">専門チャンネル</p>
      </div>
    );

    if (link) {
      return (
        <Link
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className="block"
        >
          {content}
        </Link>
      );
    }

    return content;
  }

  // Creator card
  const cardContent = (
    <>
      <div className="mx-auto mb-3 flex size-[72px] items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-pp-green-subtle to-pp-bg-tertiary shadow-lg">
        {avatarUrl ? (
          <Image
            src={avatarUrl}
            alt={`${name}のプロフィール画像`}
            width={72}
            height={72}
            className="size-full object-cover"
          />
        ) : (
          <span className="text-2xl font-bold text-pp-green-primary">👤</span>
        )}
      </div>
      <h3 className="mb-1 text-lg font-bold text-pp-green-primary">{name}</h3>
    </>
  );

  if (link) {
    return (
      <Link
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        className="block rounded-2xl bg-white p-6 text-center shadow-md transition-all duration-300 hover:-translate-y-2 hover:shadow-xl"
      >
        {cardContent}
      </Link>
    );
  }

  return (
    <div className="rounded-2xl bg-white p-6 text-center shadow-md transition-all duration-300 hover:-translate-y-2 hover:shadow-xl">
      {cardContent}
    </div>
  );
};

const CreatorsSection = () => {
  const creators: CreatorCardType[] = [
    // 日本株グループ
    {
      name: '日本株',
      isCategory: true,
      avatarUrl:
        'https://assets.postprime.com/images/d7d5f4f7-20f6-40b2-a61f-779072bdc834.jpg',
      link: 'https://postprime.com/PostPrimeJP',
      borderColor: '#3b82f6',
      bgColor: '#eff6ff',
      textColor: '#1d4ed8',
    },
    {
      name: 'かっちゃん',
      avatarUrl:
        'https://assets.postprime.com/images/4633c7ed-cd59-42ba-9637-5dbc9d821f65.jpg',
      link: 'https://postprime.com/katchan',
    },
    {
      name: 'クレヤ',
      avatarUrl: '/images/about/creator-kureya.png',
      link: 'https://postprime.com/J4ZyfmwamWPD5',
    },
    {
      name: 'ZAGI',
      avatarUrl:
        'https://assets.postprime.com/images/97add6eb-8eee-4720-b7a2-5a0c1d579b22.jpg',
      link: 'https://postprime.com/tsVCpNF2G46Tk',
    },
    // 米国株グループ
    {
      name: '米国株',
      isCategory: true,
      avatarUrl:
        'https://assets.postprime.com/images/d28d6c50-b06c-4b36-bbf0-b7025c7b3889.jpg',
      link: 'https://postprime.com/PostPrimeUS',
      borderColor: '#1e3a8a',
      bgColor: '#eff6ff',
      textColor: '#1e3a8a',
    },
    {
      name: 'くーちゃん',
      avatarUrl: '/images/about/creator-kuchan.png',
      link: 'https://postprime.com/mini_kuchan',
    },
    {
      name: '米国株投資マット',
      avatarUrl: '/images/about/creator-matt.png',
      link: 'https://postprime.com/standbyme',
    },
    {
      name: 'ミニフィ',
      avatarUrl:
        'https://assets.postprime.com/images/c7e39c68-23a5-4252-8487-e0d49349e7f9.jpg',
      link: 'https://postprime.com/minifystyle',
    },
    // 暗号資産グループ
    {
      name: '暗号資産',
      isCategory: true,
      avatarUrl:
        'https://assets.postprime.com/images/79aaaef3-0e7b-4dc2-8f01-124dd5b91763.jpg',
      link: 'https://postprime.com/crypto',
      borderColor: '#f97316',
      bgColor: '#fff7ed',
      textColor: '#ea580c',
    },
    {
      name: 'あきと',
      avatarUrl:
        'https://assets.postprime.com/images/cca706fc-70a7-4451-9633-a6a5ce815b66.jpg',
      link: 'https://postprime.com/dBOoUSgF5GxoB',
    },
    {
      name: 'EbiTen',
      avatarUrl: '/images/about/creator-ebiten.png',
      link: 'https://postprime.com/EbiTen',
    },
    {
      name: 'ぷらは',
      avatarUrl:
        'https://assets.postprime.com/images/fa306c10-85d3-4007-ba52-37f2ef429b4a.jpg',
      link: 'https://postprime.com/primehack',
    },
    // コモディティグループ
    {
      name: '商品コモディティ',
      isCategory: true,
      avatarUrl:
        'https://assets.postprime.com/images/794c7972-1287-4a41-bf69-214a5ddbef70.jpg',
      link: 'https://postprime.com/cmdty',
      borderColor: '#d4af37',
      bgColor: '#fffbea',
      textColor: '#b45309',
    },
    {
      name: '教養チャンネル',
      avatarUrl:
        'https://assets.postprime.com/images/bb5ea017-0ebb-47a4-835f-818ea9f810e2.jpg',
      link: 'https://postprime.com/kyoyoInvestor',
    },
    {
      name: 'dantakahashi',
      avatarUrl:
        'https://assets.postprime.com/images/994e5056-57de-43f4-bf55-42166935f45d.jpg',
      link: 'https://postprime.com/dantakahashi',
    },
    {
      name: 'Mu',
      avatarUrl: '/images/about/creator-mu.png',
      link: 'https://postprime.com/Y9MfZkvpUbNrz',
    },
  ];

  return (
    <section
      id="prime-section"
      className="w-full bg-white px-6 py-20"
      style={{ scrollMarginTop: '110px' }}
    >
      <div className="mx-auto max-w-7xl">
        <h2 className="mb-4 text-center text-3xl font-bold text-pp-text-primary md:text-4xl">
          注目のクリエイター
        </h2>
        <p className="mb-8 text-center text-base text-pp-text-secondary md:text-lg">
          各分野のトップクリエイターが情報を発信しています
        </p>

        {/* Prime Info Box */}
        <div className="mx-auto mb-12 max-w-5xl rounded-xl border-l-4 border-pp-green-primary bg-pp-bg-secondary p-6">
          <p className="text-base leading-relaxed text-pp-text-secondary">
            <strong className="text-pp-green-primary">プライム登録とは</strong>
            各クリエイターや専門チャンネルを閲覧するための月額定額型のコンテンツ配信サービスです。プライムクリエイターや専門チャンネルの投稿やLIVEを視聴するには、プライム登録が必要です。
          </p>
        </div>

        {/* Creators Grid */}
        <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
          {creators.map((creator, index) => (
            <CreatorCard
              key={index}
              name={creator.name}
              avatarUrl={creator.avatarUrl}
              isCategory={creator.isCategory}
              link={creator.link}
              emoji={creator.emoji}
              borderColor={creator.borderColor}
              bgColor={creator.bgColor}
              textColor={creator.textColor}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default CreatorsSection;
